import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Megaphone, Sparkles, Rocket, ArrowRight, Image as ImageIcon, Check } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [
    { days:  30, label: "30 days"  },
    { days:  60, label: "60 days · 2 months" },
    { days:  90, label: "90 days · 3 months" },
    { days: 180, label: "180 days · 6 months" },
];


export default function AdvertisePage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [pricing, setPricing] = useState(null);
    const [slot, setSlot] = useState("landing_hero");
    const [durationDays, setDurationDays] = useState(30);
    const [businessName, setBusinessName] = useState("");
    const [headline, setHeadline] = useState("");
    const [body, setBody] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [linkUrl, setLinkUrl] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [myAds, setMyAds] = useState([]);

    useEffect(() => {
        api.get("/ads/pricing").then((r) => setPricing(r.data)).catch(() => setPricing(null));
    }, []);

    useEffect(() => {
        if (!user) return;
        api.get("/admin/ads")
            .then((r) => setMyAds(Array.isArray(r.data) ? r.data.filter((a) => a.advertiser_id === user.id) : []))
            .catch(() => setMyAds([]));
    }, [user]);

    const slotPrice = pricing?.slots?.find((s) => s.id === slot)?.price_jmd_per_month || 0;
    const totalPrice = Math.max(1, Math.floor(durationDays / 30)) * slotPrice;

    const submit = async (e) => {
        e.preventDefault();
        if (!user) {
            navigate("/auth?mode=register&next=/advertise");
            return;
        }
        if (!businessName.trim() || !headline.trim() || !linkUrl.trim()) {
            toast.error("Business name, headline and link URL are required.");
            return;
        }
        setSubmitting(true);
        try {
            const { data: ad } = await api.post("/ads/apply", {
                slot,
                headline: headline.trim(),
                body: body.trim(),
                image_url: imageUrl.trim() || null,
                link_url: linkUrl.trim(),
                business_name: businessName.trim(),
                duration_days: durationDays,
            });
            toast.success("Ad created — redirecting to secure checkout…");
            const origin = window.location.origin;
            const { data: checkout } = await api.post("/payments/handypay/checkout", {
                ad_id: ad.ad.id,
                origin_url: origin,
            });
            window.location.href = checkout.url;
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="advertise-page">
            <Header />

            <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-14 lg:pt-20 pb-20">
                {/* Hero */}
                <div className="text-center mb-14">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FFD700]/40 text-[10px] uppercase tracking-[0.3em] text-amber-300/90 mb-5">
                        <Megaphone size={11} /> Advertise with SMART
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
                        Reach Jamaica's <span className="gold-text italic">premium</span> on-demand audience.
                    </h1>
                    <p className="mt-5 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                        Three slot types across the SMART funnel. Your banner appears to every
                        high-intent customer the moment they land, browse services or shop the marketplace.
                        No agency fees, no bidding wars — flat monthly rate, pay once, go live.
                    </p>
                </div>

                {/* Slot picker */}
                <div className="grid gap-4 md:grid-cols-3 mb-10" data-testid="advertise-slot-picker">
                    {(pricing?.slots || []).map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            data-testid={`advertise-slot-${s.id}`}
                            onClick={() => setSlot(s.id)}
                            className={`text-left rounded-xl border p-5 transition-all ${
                                slot === s.id
                                    ? "border-[#FFD700] bg-gradient-to-br from-[#2d1a05] to-background shadow-[0_0_24px_-10px_rgba(255,215,0,0.5)]"
                                    : "border-white/10 bg-black/30 hover:border-[#FFD700]/40"
                            }`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[10px] uppercase tracking-[0.26em] text-amber-300/80">{s.label}</div>
                                {slot === s.id && <Check size={14} className="text-[#FFD700]" />}
                            </div>
                            <div className="font-serif text-2xl text-foreground">
                                JMD ${s.price_jmd_per_month.toLocaleString()}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">/ month</div>
                            <div className="text-xs text-muted-foreground mt-3 leading-relaxed">
                                {s.id === "landing_hero"
                                    ? "Shown directly below the hero on smartdemands247.com. Highest impressions."
                                    : s.id === "marketplace_top"
                                    ? "Pinned above the marketplace grid. High purchase intent."
                                    : "On every /services page. Reaches ready-to-book customers."}
                            </div>
                        </button>
                    ))}
                </div>

                <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-8" data-testid="advertise-form">
                    {/* Form */}
                    <div className="bg-card border border-white/10 p-6 lg:p-8 rounded-sm space-y-5">
                        <h2 className="font-serif text-2xl mb-1">Your creative</h2>
                        <p className="text-xs text-muted-foreground mb-4">What people will see when your ad is live.</p>

                        <Field label="Business name *">
                            <Input
                                value={businessName}
                                onChange={(e) => setBusinessName(e.target.value)}
                                placeholder="e.g. Island Crave Catering"
                                className="bg-input border-white/10 h-11 rounded-sm"
                                maxLength={80}
                                data-testid="advertise-business-name"
                                required
                            />
                        </Field>
                        <Field label="Headline (90 chars max) *">
                            <Input
                                value={headline}
                                onChange={(e) => setHeadline(e.target.value)}
                                placeholder="e.g. Weekend brunch delivered to your door"
                                className="bg-input border-white/10 h-11 rounded-sm"
                                maxLength={90}
                                data-testid="advertise-headline"
                                required
                            />
                        </Field>
                        <Field label="Supporting line (180 chars max)">
                            <Textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder="One short line underneath — a benefit or offer."
                                className="bg-input border-white/10 rounded-sm"
                                maxLength={180}
                                rows={2}
                                data-testid="advertise-body"
                            />
                        </Field>
                        <Field label="Image URL (optional)">
                            <Input
                                value={imageUrl}
                                onChange={(e) => setImageUrl(e.target.value)}
                                placeholder="https://..."
                                className="bg-input border-white/10 h-11 rounded-sm"
                                data-testid="advertise-image-url"
                                type="url"
                            />
                        </Field>
                        <Field label="Where should clicks go? *">
                            <Input
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                placeholder="https://your-site.com or /marketplace/your-listing-id"
                                className="bg-input border-white/10 h-11 rounded-sm"
                                data-testid="advertise-link-url"
                                required
                            />
                        </Field>

                        <Field label="How long should it run?">
                            <div className="grid grid-cols-2 gap-2" data-testid="advertise-duration">
                                {DURATIONS.map((d) => (
                                    <button
                                        type="button"
                                        key={d.days}
                                        onClick={() => setDurationDays(d.days)}
                                        data-testid={`advertise-duration-${d.days}`}
                                        className={`px-3 py-2.5 rounded-md border text-xs uppercase tracking-[0.22em] transition-colors ${
                                            durationDays === d.days
                                                ? "border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700]"
                                                : "border-white/10 text-muted-foreground hover:text-foreground"
                                        }`}
                                    >
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    </div>

                    {/* Live preview + totals */}
                    <aside className="space-y-6">
                        <div className="bg-card border border-[#FFD700]/30 rounded-sm p-5" data-testid="advertise-preview">
                            <div className="overline mb-3">Live preview</div>
                            <div className="rounded-xl border border-[#FFD700]/40 bg-gradient-to-r from-[#2d1a05] via-[#1a1003] to-background p-4 flex items-center gap-3">
                                {imageUrl ? (
                                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-black/40 border border-white/10 shrink-0">
                                        <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                                    </div>
                                ) : (
                                    <div className="w-14 h-14 rounded-lg bg-black/40 border border-white/10 shrink-0 grid place-items-center text-muted-foreground">
                                        <ImageIcon size={18} />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] uppercase tracking-[0.3em] text-amber-300/70 mb-0.5">
                                        {(businessName || "Your business").slice(0, 40)} · Sponsored
                                    </div>
                                    <div className="text-sm text-foreground font-medium truncate">
                                        {headline || "Your headline shows here"}
                                    </div>
                                    {body && (
                                        <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
                                            {body}
                                        </div>
                                    )}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.26em] text-[#FFD700] shrink-0 pr-2">
                                    See more →
                                </div>
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-3">
                                This is how it appears live
                            </p>
                        </div>

                        <div className="bg-card border border-white/10 rounded-sm p-5">
                            <div className="overline mb-4">Order summary</div>
                            <SumRow label="Slot" value={pricing?.slots?.find((s) => s.id === slot)?.label || slot} />
                            <SumRow label="Rate" value={`JMD $${slotPrice.toLocaleString()} / month`} />
                            <SumRow label="Duration" value={`${durationDays} days`} />
                            <div className="hairline my-4" />
                            <SumRow label="Total" value={`JMD $${totalPrice.toLocaleString()}`} big />
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="w-full mt-5 bg-[#FFD700] text-black hover:bg-amber-300 rounded-sm uppercase tracking-[0.22em] text-xs h-12 font-semibold"
                                data-testid="advertise-submit"
                            >
                                {submitting ? "Opening checkout…" : "Pay & go live"} <ArrowRight size={14} className="ml-2" />
                            </Button>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-3 text-center">
                                Secure HandyPay checkout
                            </div>
                        </div>

                        {myAds.length > 0 && (
                            <div className="bg-card border border-white/10 rounded-sm p-5" data-testid="my-ads-panel">
                                <div className="overline mb-3">My past ads</div>
                                <ul className="space-y-2 text-xs">
                                    {myAds.slice(0, 6).map((a) => (
                                        <li key={a.id} className="flex items-center justify-between gap-2 py-1 border-b border-white/5 last:border-b-0" data-testid={`my-ad-${a.id}`}>
                                            <div className="truncate flex-1">
                                                <div className="text-foreground truncate">{a.headline}</div>
                                                <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">{a.slot_label}</div>
                                            </div>
                                            <span className={`text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border ${
                                                a.status === "active"
                                                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                                                    : a.status === "pending"
                                                    ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                                                    : "border-white/10 text-muted-foreground"
                                            }`}>
                                                {a.status}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </aside>
                </form>
            </section>

            <Footer />
        </div>
    );
}


function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-2 block">{label}</Label>
            {children}
        </div>
    );
}

function SumRow({ label, value, big = false }) {
    return (
        <div className="flex items-baseline justify-between py-1.5">
            <div className="text-[10px] uppercase tracking-[0.26em] text-muted-foreground">{label}</div>
            <div className={big ? "font-serif text-2xl gold-text" : "text-sm text-foreground"}>{value}</div>
        </div>
    );
}
